import React, { useEffect, useState } from "react";
import Navigation from "./Navigation";
import Footer from "./Footer";

const BOOKING_HREF =
  "mailto:enquiry@onsideai.org?subject=Sentinel%20discovery%20call";

const SECTORS = [
  "Energy",
  "Oil & Gas",
  "Mining",
  "Heavy Industry",
  "Manufacturing",
  "Logistics",
  "Ports & Cargo",
  "Transportation",
  "Banking",
  "Retail",
  "Telecom",
  "Healthcare",
  "Education",
  "Public Infrastructure",
  "Government",
  "Critical Sites",
];

const HERO_RULES = [
  {
    text: "tell me if someone is drawing graffiti",
    tag: "Public Infrastructure",
  },
  {
    text: "tell me if there is a gun",
    tag: "Schools",
  },
  {
    text: "tell me if someone falls down on the ground",
    tag: "Healthcare",
  },
];

const CONFIG_ROWS = [
  {
    lead: "Works with your cameras.",
    body: "No rip-and-replace. Sentinel runs on the cameras you already own.",
  },
  {
    lead: "Integrates with your VMS.",
    body: "Milestone channel partner with native integration. Connectors available for any other VMS your team runs.",
  },
  {
    lead: "Cloud, on-prem, or hybrid.",
    body: "Deploy where it fits your security posture. Same product, same prompts, same alerts — wherever the workload lives.",
  },
  {
    lead: "Stays on your network.",
    body: "Encrypted end-to-end and authenticated on both ends. No public internet exposure.",
  },
  {
    lead: "Onboarding and training available.",
    body: "White-glove deployment. 24/7/365 support. Continuous tuning included.",
  },
];

const Sentinel: React.FC = () => {
  const [ruleStates, setRuleStates] = useState<boolean[]>(
    HERO_RULES.map(() => true)
  );
  const [cfgStates, setCfgStates] = useState<boolean[]>(
    CONFIG_ROWS.map(() => true)
  );

  const toggleRule = (i: number) =>
    setRuleStates((s) => s.map((v, j) => (i === j ? !v : v)));
  const toggleCfg = (i: number) =>
    setCfgStates((s) => s.map((v, j) => (i === j ? !v : v)));

  const cfgActive = cfgStates.filter(Boolean).length;

  useEffect(() => {
    const els = document.querySelectorAll(".sentinel-console .reveal");
    const obs = new IntersectionObserver(
      (entries, o) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("show");
            o.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="sentinel-page sentinel-console">
      <nav className="home-header">
        <Navigation />
      </nav>

      {/* HERO */}
      <section className="sn-hero">
        <div className="sn-hero-bg" aria-hidden="true">
          <div className="sn-hero-grid-lines" />
          <div className="sn-hero-glow" />
        </div>

        <div className="sn-container sn-hero-grid">
          <div className="sn-hero-content reveal">
            <p className="sn-eyebrow">
              <span className="bracket">[</span> ONSIDE&nbsp;/&nbsp;SENTINEL{" "}
              <span className="status-dot" /> LIVE <span className="bracket">]</span>
            </p>

            <h1>
              A new way <span className="ink-soft">to watch.</span>
            </h1>

            <p className="sn-subhead">
              Real-time AI on the cameras you already own.
            </p>

            <p className="sn-lede">
              Operators write a plain-English rule. Every camera watches for it.
              Live in seconds.
            </p>

            <div className="sn-hero-rules">
              <p className="sn-hero-rules-label">ACTIVE PROMPTS · SAMPLE</p>
              {HERO_RULES.map((rule, i) => {
                const on = ruleStates[i];
                return (
                  <div
                    key={rule.text}
                    className={`sn-rule-pill${on ? "" : " is-off"}`}
                  >
                    <span className="rule-mark" />
                    <div className="rule-body">
                      <span className="rule-text">{rule.text}</span>
                      <span className="rule-tag">{rule.tag}</span>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={on}
                      aria-label={`${
                        on ? "Disable" : "Enable"
                      } rule: ${rule.text}`}
                      className={`rule-state${on ? " on" : " off"}`}
                      onClick={() => toggleRule(i)}
                    >
                      {on ? "ON" : "OFF"}
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="sn-hero-ctas">
              <a className="btn-indigo" href={BOOKING_HREF}>
                Book a discovery call
                <span className="arrow">→</span>
              </a>
              <a className="btn-ghost" href="#how-it-works">
                See it in action
              </a>
            </div>
          </div>

          <div className="sn-hero-frame reveal">
            <div className="sn-hero-frame-chrome">
              <span className="dot red" />
              <span className="dot yellow" />
              <span className="dot green" />
              <span className="chrome-spacer" />
              <span className="chrome-status">
                <span className="status-dot" /> 3 of 16 ONLINE
              </span>
            </div>
            <img
              className="sn-hero-frame-img"
              src="/images/sentinel-step-02-monitor.png"
              alt="Sentinel live operator dashboard with multiple camera feeds and active alerts"
            />
          </div>
        </div>
      </section>

      {/* PARTNERS — Milestone */}
      <section className="sn-partners reveal">
        <div className="sn-container">
          <div className="sn-partner-strip">
            <p className="sn-partner-strip-label">
              <span className="bracket">[</span> OFFICIAL TECHNOLOGY PARTNER{" "}
              <span className="bracket">]</span>
            </p>
            <div className="sn-partner-strip-logo">
              <img src="/images/milestone-logo.png" alt="Milestone Systems" />
            </div>
          </div>
        </div>
      </section>

      {/* THE SHIFT */}
      <section className="sn-shift reveal">
        <div className="sn-container">
          <p className="sn-eyebrow">
            <span className="bracket">[</span> THE SHIFT{" "}
            <span className="bracket">]</span>
          </p>
          <h2>
            Not a better camera. <span className="indigo">A new category.</span>
          </h2>

          <div className="sn-shift-grid">
            <article className="sn-card sn-card-old">
              <p className="sn-card-label">{"// THE OLD WAY"}</p>
              <h3>Vendors build the model.</h3>
              <ul>
                <li>Weeks. Months.</li>
                <li>Custom-dev cycles.</li>
                <li>Static datasets.</li>
                <li>Limited library of detections.</li>
              </ul>
              <p className="sn-card-foot">REQUIRES VENDOR</p>
            </article>

            <article className="sn-card sn-card-new">
              <div className="sn-card-flag">SENTINEL</div>
              <p className="sn-card-label">{"// WITH SENTINEL"}</p>
              <h3>Operators write the rule.</h3>
              <ul>
                <li>Live the same day.</li>
                <li>Any sentence. Any camera.</li>
                <li>Plain-English rules.</li>
                <li>Every camera becomes an analyst.</li>
              </ul>
              <p className="sn-card-foot">SELF-SERVICE</p>
            </article>
          </div>
        </div>
      </section>

      {/* WALKTHROUGH */}
      <section id="how-it-works" className="sn-walk">
        <div className="sn-container">
          <div className="sn-walk-head reveal">
            <p className="sn-eyebrow">
              <span className="bracket">[</span> HOW IT WORKS{" "}
              <span className="bracket">]</span>
            </p>
            <h2>From sentence to signal.</h2>
            <p className="sn-section-sub">
              Three steps. You write the first. The next two happen without
              you.
            </p>
          </div>

          <ol className="sn-steps">
            <li className="sn-step reveal">
              <div className="sn-step-meta">
                <span className="sn-step-num">01</span>
                <span className="sn-step-rule" />
                <span className="sn-step-tag">PROMPT</span>
              </div>
              <div className="sn-step-grid">
                <div className="sn-step-text">
                  <h3>Type a rule.</h3>
                  <p>
                    Operators type in plain English. No models to train, no
                    code to write, no data scientists to hire.
                  </p>
                  <div className="sn-prompt-card">
                    <span className="sn-prompt-arrow">&gt;</span>
                    <span className="sn-prompt-text">
                      tell me if someone falls down on the ground
                    </span>
                    <span className="sn-prompt-cursor" />
                  </div>
                  <p className="sn-step-foot">
                    Sentinel expands the rule, picks the cameras, and starts
                    watching the next frame.
                  </p>
                </div>
                <figure className="sn-step-frame sn-step-frame-32">
                  <div className="sn-step-frame-chrome">
                    <span className="chrome-title">
                      configuration · queries
                    </span>
                  </div>
                  <img
                    src="/images/sentinel-step-01-prompt.png"
                    alt="Operator typing a plain-English detection rule into Sentinel's configuration panel"
                  />
                </figure>
              </div>
            </li>

            <li className="sn-step sn-step-peak reveal">
              <div className="sn-step-meta">
                <span className="sn-step-num">02</span>
                <span className="sn-step-rule" />
                <span className="sn-step-tag">WATCH</span>
              </div>
              <div className="sn-step-grid sn-step-grid-stacked">
                <div className="sn-step-text">
                  <h3>Watch every camera.</h3>
                  <p>
                    Sentinel scores every frame from every assigned camera
                    against your rule, in real time. Hundreds of rules can run
                    in parallel.
                  </p>
                  <div className="sn-status-row">
                    <span className="sn-status-pill">
                      <span className="status-dot" /> 3 of 16 ONLINE
                    </span>
                    <span className="sn-status-pill alert">
                      <span className="status-dot red" /> 6 ALERTS
                    </span>
                    <span className="sn-status-pill mute">24 FPS</span>
                  </div>
                </div>
                <figure className="sn-step-frame sn-step-frame-169 sn-step-frame-wide">
                  <div className="sn-step-frame-chrome">
                    <span className="chrome-spacer" />
                    <span className="chrome-status">
                      <span className="status-dot" /> LIVE
                    </span>
                  </div>
                  <img
                    src="/images/sentinel-step-02-monitor.png"
                    alt="Sentinel's live operator dashboard showing camera feeds and real-time alerts"
                  />
                </figure>
              </div>
            </li>

            <li className="sn-step sn-step-rev reveal">
              <div className="sn-step-meta">
                <span className="sn-step-num">03</span>
                <span className="sn-step-rule" />
                <span className="sn-step-tag">ALERT</span>
              </div>
              <div className="sn-step-grid">
                <figure className="sn-step-frame sn-step-frame-32">
                  <div className="sn-step-frame-chrome">
                    <span className="chrome-title">
                      incident · evidence frame
                    </span>
                    <span className="chrome-conf">85% conf</span>
                  </div>
                  <img
                    src="/images/sentinel-step-03-alert.png"
                    alt="Sentinel alert detail showing the matched evidence frame and the rule that fired"
                  />
                </figure>
                <div className="sn-step-text">
                  <h3>Get the evidence.</h3>
                  <p>
                    When a rule fires you get the frame, the observation, the
                    confidence, and the timestamp. Decide once. Move on.
                  </p>
                  <div className="sn-evidence-strip">
                    <span className="ev-key">Rule</span>
                    <span className="ev-val">
                      tell me if someone falls down on the ground
                    </span>
                  </div>
                  <div className="sn-evidence-strip">
                    <span className="ev-key">Confidence</span>
                    <span className="ev-val">85%</span>
                  </div>
                </div>
              </div>
            </li>
          </ol>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="sn-manifesto reveal">
        <div className="sn-container narrow">
          <h2>Autonomous monitoring.</h2>
          <p className="sn-manifesto-body">
            Most cameras record. Few alert. None understand. Sentinel turns
            every camera you already own into an analyst that watches without
            being asked &mdash; for the situations you decide matter, in the
            words you choose to describe them.
          </p>
        </div>
      </section>

      {/* SECTORS */}
      <section className="sn-sectors reveal">
        <div className="sn-container">
          <p className="sn-eyebrow">
            <span className="bracket">[</span> WHERE IT WORKS{" "}
            <span className="bracket">]</span>
          </p>
          <h2>Every sector that owns cameras.</h2>

          <div className="sn-tag-grid">
            {SECTORS.map((s) => (
              <span className="sn-tag" key={s}>
                <span className="sn-tag-dot" />
                {s}
              </span>
            ))}
          </div>

          <p className="sn-sectors-closer">
            Hundreds of pre-built prompts. Any sentence you can write becomes a
            sensor.
          </p>
        </div>
      </section>

      {/* ON YOUR TERMS — settings panel mock */}
      <section className="sn-terms reveal">
        <div className="sn-container narrow">
          <p className="sn-eyebrow">
            <span className="bracket">[</span> ON YOUR TERMS{" "}
            <span className="bracket">]</span>
          </p>
          <h2>Built to fit your infrastructure.</h2>

          <div className="sn-config-panel">
            <div className="sn-config-header">
              <span className="sn-config-title">
                <span className="status-dot" /> SENTINEL · DEPLOYMENT CONFIG
              </span>
              <span className="sn-config-meta">
                {cfgActive} / {CONFIG_ROWS.length} ACTIVE
              </span>
            </div>

            <ol className="sn-config-list">
              {CONFIG_ROWS.map((row, i) => {
                const on = cfgStates[i];
                return (
                  <li
                    key={row.lead}
                    className={on ? undefined : "is-off"}
                  >
                    <span className="cfg-num">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="cfg-text">
                      <h4>{row.lead}</h4>
                      <p>{row.body}</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={on}
                      aria-label={`${on ? "Disable" : "Enable"}: ${row.lead}`}
                      className={`cfg-toggle${on ? " on" : " off"}`}
                      onClick={() => toggleCfg(i)}
                    />
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="sn-final reveal">
        <div className="sn-container">
          <h2>See it on your cameras.</h2>
          <p className="sn-final-sub">
            <em>Real footage. Real prompts. Real alerts.</em>
          </p>
          <p className="sn-final-body">
            Schedule a discovery call &mdash; we&rsquo;ll show you Sentinel
            running on a sector that mirrors yours.
          </p>
          <a className="btn-indigo btn-large" href={BOOKING_HREF}>
            Book a discovery call
            <span className="arrow">→</span>
          </a>
          <p className="sn-final-mark">
            <span className="line">OnSide AI Inc.</span>
            <span className="line">Think it. Prompt it. Protected.</span>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Sentinel;
